import type { Skill } from '@clockwise/clients/skills';
import { ApiError, countryName, formatPhone, LifecycleStatus } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ScopedAuditPanel, useScopedAudit } from '@/components/custom/audit/scoped-audit';
import { BackLink } from '@/components/custom/back-link';
import { BulkAddSkillsSheet } from '@/components/custom/bulk-add-skills-sheet';
import { CustomFieldsViewer } from '@/components/custom/custom-fields-viewer';
import {
  CHANGED_RULE_CLASS,
  FieldValue,
  PreviousValues,
  useFieldChanged,
} from '@/components/custom/detail-list';
import {
  diffTarget,
  EntityVersionBar,
  previousValues,
  selectedVersion,
  useEntityVersions,
  withVersionValues,
} from '@/components/custom/entity-version-history';
import { LifecycleStatusBadge } from '@/components/custom/lifecycle-status-badge';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { Section } from '@/components/custom/section';
import { SkillAutocomplete } from '@/components/custom/skill-autocomplete';
import { UserChangeEmailSheet } from '@/components/custom/user-change-email-sheet';
import { UserCustomerTeamSection } from '@/components/custom/user-customer-team-section';
import { UserDirectReportsSection } from '@/components/custom/user-direct-reports-section';
import { UserFormSheet } from '@/components/custom/user-form-sheet';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  departmentsApi,
  serviceRolesApi,
  systemRolesApi,
  userEmployeeCostsApi,
  usersApi,
} from '@/lib/api';
import { useMe } from '@/lib/me';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { userDisplayName, userStatus } from '@/lib/user-display';
import { cn } from '@/lib/utils';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];
const DEFAULT_TAB: TabValue = 'details';

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
  // Which version of the record the Details tab is showing. Absent = current.
  // In the URL so a specific version is linkable and survives a reload.
  version: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute('/_authenticated/users/$id/')({
  validateSearch: detailSearch,
  component: UserDetailPage,
});

function UserDetailPage() {
  const { id } = Route.useParams();
  const { tab, version: requestedVersion } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { user: me, loading: meLoading } = useMe();

  const detail = useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => usersApi.get(id),
  });
  const liveUser = detail.data?.data ?? null;

  // Record history. `user` below is what the page RENDERS — the live record,
  // or a past version's values laid over it — so every field, and the role /
  // department lookups keyed off its ids, follow the selected version without
  // each one having to know about versioning. `liveUser` is kept for the few
  // things that must stay present-tense (the edit affordances).
  const versionsQuery = useEntityVersions('user', id);
  const viewedVersion = selectedVersion(versionsQuery.data?.data, requestedVersion);
  const user = liveUser ? withVersionValues(liveUser, viewedVersion, () => ({})) : null;

  // "Show what changed" for the CURRENT version, off by default. Lives here
  // rather than inside the bar because `diffTarget` and `previousValues` below
  // must agree with the toggle — two copies of the flag could disagree and
  // strike through rows the banner says nothing about.
  const [showCurrentDiff, setShowCurrentDiff] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const skillsQuery = useQuery({
    queryKey: queryKeys.users.skills(id),
    queryFn: () => usersApi.listSkills(id),
  });
  const skills = skillsQuery.data?.data ?? [];

  // Look up role + manager labels so the details panel reads as English.
  // `enabled` guards the queries; the queryFn args fall back to '' (never
  // called) so we don't need non-null assertions.
  const systemRole = useQuery({
    queryKey: queryKeys.systemRoles.detail(user?.systemRoleId ?? ''),
    queryFn: () => systemRolesApi.get(user?.systemRoleId ?? ''),
    enabled: Boolean(user?.systemRoleId),
  });
  const serviceRole = useQuery({
    queryKey: queryKeys.serviceRoles.detail(user?.serviceRoleId ?? ''),
    queryFn: () => serviceRolesApi.get(user?.serviceRoleId ?? ''),
    enabled: Boolean(user?.serviceRoleId),
  });
  // Manager + SR Controller display names come from the detail response
  // (`related`), resolved server-side. Do NOT fetch the referenced record via
  // usersApi.get — that endpoint is view-scope-gated and 403s (→ stuck
  // "loading…") when the manager sits outside the caller's scope.
  const managerName = viewedVersion
    ? (viewedVersion.labels.managerId ?? null)
    : (detail.data?.related.managerName ?? null);
  const srControllerName = viewedVersion
    ? (viewedVersion.labels.srControllerId ?? null)
    : (detail.data?.related.srControllerName ?? null);
  const customerTeamCount = detail.data?.related.customerTeamCount ?? 0;
  const department = useQuery({
    queryKey: queryKeys.departments.detail(user?.departmentId ?? ''),
    queryFn: () => departmentsApi.get(user?.departmentId ?? ''),
    enabled: Boolean(user?.departmentId),
  });
  // Cost visibility is decided server-side: presence of user.cost.view AND
  // this employee within its HR-Managed scope (related.costVisible). When
  // false we still surface a LOCKED placeholder so the field is discoverable
  // without leaking the value — see the cost Section below.
  const costVisible = detail.data?.related.costVisible ?? false;
  const costHistory = useQuery({
    queryKey: queryKeys.userEmployeeCosts.history(id),
    queryFn: () => userEmployeeCostsApi.list({ userId: id }),
    enabled: costVisible,
  });
  // The API returns history newest-first (effective_from DESC) so the
  // current rate lands at the top of the table without a client-side
  // re-sort.
  const costRows = costHistory.data?.data ?? [];

  // Customer-team section visibility. The backend route accepts three
  // callers: the profile owner, holders of `customer.team.view`, AND
  // delegation leads (see apps/api/src/core/customer_team/routes.ts). The
  // FE only models the first two here — delegation leads viewing a
  // delegate's profile see a silent gap; #126 review tracked this as a
  // known scoping limitation pending a `useCanViewTeamFor(id)` hook.
  //
  // While `useMe()` is still resolving we render `null` (a skeleton in
  // the JSX) instead of `false` — otherwise an own-profile viewer flashes
  // a hidden section while the auth probe warms up.
  const canViewCustomerTeam = useHasPermission('customer.team.view');
  const showCustomerTeam: boolean | null = meLoading ? null : me?.id === id || canViewCustomerTeam;

  // Whether the caller may edit THIS employee — presence of user.edit AND the
  // employee within its edit scope (related.editable, server-derived). Skills
  // add/bulk-add/remove and the Edit button all hit edit-scoped routes, so we
  // gate them on this rather than bare permission presence: a managed /
  // customer-team editor viewing an out-of-scope employee sees no edit controls
  // instead of buttons the API would reject.
  const editable = (detail.data?.related.editable ?? false) && !viewedVersion;
  const canEditSkills = editable;

  // Address (PII) visibility is decided server-side: presence of user.pii.view
  // OR the caller's own profile (related.addressVisible). When false the address
  // fields on `data` are redacted (nulled) and we render a LOCKED placeholder so
  // the section is discoverable without leaking the value — see the Address
  // Section below.
  const addressVisible = detail.data?.related.addressVisible ?? false;

  // Audit history. `audit.view` gates the endpoint; the server additionally
  // gates each entity_type by its own `.view`, so cost rows only appear for a
  // caller with user.cost.view and address-field diffs are redacted without
  // user.pii.view — the section never out-reveals the page above it.
  const canViewAudit = useHasPermission('audit.view');
  // Admin-tier: amending a version restates what every report over that period
  // has always said, which `user.edit` alone does not authorise.
  const canAmendHistory = useHasPermission('user.admin');
  const audit = useScopedAudit({ scopeType: 'users', scopeId: id, enabled: canViewAudit });

  // Don't land on a bookmarked ?tab=audit when the caller lacks audit.view —
  // fall back to details.
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  // ---- skills mutations ----------------------------------------------------

  const [bulkOpen, setBulkOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [addPending, setAddPending] = useState(false);

  // Add/remove are idempotent server-side; a no-op (already-present add,
  // already-absent remove) returns 200 with an unchanged list. We capture
  // the pre-mutation list length so the toast only fires when something
  // actually changed.
  const skillsCount = () =>
    (queryClient.getQueryData(queryKeys.users.skills(id)) as { data: Skill[] } | undefined)?.data
      .length ?? 0;

  const addSkill = useMutation({
    mutationFn: (skillId: string) => usersApi.addSkill(id, { skillId }),
    onMutate: () => ({ prevCount: skillsCount() }),
    onSuccess: (res, _vars, ctx) => {
      queryClient.setQueryData(queryKeys.users.skills(id), res);
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      if (res.data.length > (ctx?.prevCount ?? 0)) toast.success('Skill added');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Add failed'),
    onSettled: () => setAddPending(false),
  });

  const bulkAdd = useMutation({
    mutationFn: (skillIds: string[]) => usersApi.bulkAddSkills(id, { skillIds }),
    onMutate: () => ({ prevCount: skillsCount() }),
    onSuccess: (res, _vars, ctx) => {
      queryClient.setQueryData(queryKeys.users.skills(id), res);
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      const added = res.data.length - (ctx?.prevCount ?? 0);
      if (added > 0) toast.success(`Added ${added} skill${added === 1 ? '' : 's'}`);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Bulk add failed'),
  });

  const removeSkill = useMutation({
    mutationFn: (skillId: string) => usersApi.removeSkill(id, skillId),
    onMutate: () => ({ prevCount: skillsCount() }),
    onSuccess: (res, _vars, ctx) => {
      queryClient.setQueryData(queryKeys.users.skills(id), res);
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      if (res.data.length < (ctx?.prevCount ?? 0)) toast.success('Skill removed');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Remove failed'),
  });

  const alreadyAddedIds = useMemo(() => new Set(skills.map((s) => s.id)), [skills]);

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/users">Back to Employees</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : user ? (
            <>
              <h1 className="flex items-center gap-3 text-2xl font-semibold">
                <span>{userDisplayName(user)}</span>
                <LifecycleStatusBadge
                  status={
                    userStatus(user) === 'Active'
                      ? LifecycleStatus.Active
                      : LifecycleStatus.Inactive
                  }
                />
              </h1>
              <p className="text-sm text-muted-foreground">
                {user.title ? `${user.title} · ` : ''}
                {user.email}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        {user && editable ? (
          <Button onClick={() => setEditOpen(true)}>
            <Pencil />
            Edit
          </Button>
        ) : null}
      </header>

      {detail.isLoading ? (
        <SectionsSkeleton />
      ) : user ? (
        <PillTabs
          value={activeTab}
          onValueChange={(v) =>
            navigate({
              search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
              replace: true,
            })
          }
          className="rounded-xl border bg-card p-4"
        >
          <PillTabsList fullWidth>
            <PillTabsTrigger value="details">Details</PillTabsTrigger>
            {canViewAudit ? (
              <PillTabsTrigger value="audit">
                Audit
                {audit.data ? (
                  <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                    {audit.data.meta.total}
                  </span>
                ) : null}
              </PillTabsTrigger>
            ) : null}
          </PillTabsList>

          <PillTabsContent value="details" className="space-y-6">
            <EntityVersionBar
              query={versionsQuery}
              selected={viewedVersion}
              onSelect={(v) => navigate({ search: (s) => ({ ...s, version: v }), replace: true })}
              onAmend={
                canAmendHistory
                  ? (version) =>
                      navigate({ to: '/users/$id/edit', params: { id }, search: { version } })
                  : undefined
              }
              showCurrentDiff={showCurrentDiff}
              onShowCurrentDiffChange={setShowCurrentDiff}
            />
            <PreviousValues
              values={previousValues(
                diffTarget(versionsQuery.data?.data, viewedVersion, showCurrentDiff),
              )}
            >
              <Section title="Identity" description="Display name, title, and email.">
                <DetailGrid>
                  <DetailRow field="firstName" label="First name">
                    {user.firstName}
                  </DetailRow>
                  <DetailRow field="lastName" label="Last name">
                    {user.lastName}
                  </DetailRow>
                  <DetailRow field="preferredName" label="Preferred name">
                    {user.preferredName ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="title" label="Title">
                    {user.title ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="email" label="Email">
                    <span className="inline-flex items-center gap-2">
                      <span>{user.email}</span>
                      {editable ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          aria-label="Change email address"
                          onClick={() => setEmailOpen(true)}
                        >
                          Change
                        </Button>
                      ) : null}
                    </span>
                  </DetailRow>
                  <DetailRow field="active" label="Status">
                    <span className="inline-flex items-center gap-2">
                      <LifecycleStatusBadge
                        status={
                          userStatus(user) === 'Active'
                            ? LifecycleStatus.Active
                            : LifecycleStatus.Inactive
                        }
                      />
                      {!user.active ? (
                        <span className="text-xs text-muted-foreground">manually deactivated</span>
                      ) : null}
                    </span>
                  </DetailRow>
                </DetailGrid>
              </Section>

              <Section
                title="Employment"
                description="Role, reporting, capacity, pricing, lifecycle."
              >
                <DetailGrid>
                  <DetailRow field="systemRoleId" label="System role">
                    {user.systemRoleId && systemRole.data?.data.name ? (
                      <Link
                        to="/setup/system-roles/$id"
                        params={{ id: user.systemRoleId }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {systemRole.data.data.name}
                      </Link>
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </DetailRow>
                  <DetailRow field="serviceRoleId" label="Service role">
                    {user.serviceRoleId && serviceRole.data?.data.name ? (
                      <Link
                        to="/setup/service-roles/$id"
                        params={{ id: user.serviceRoleId }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {serviceRole.data.data.name}
                      </Link>
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </DetailRow>
                  <DetailRow field="departmentId" label="Department">
                    {user.departmentId && department.data?.data?.name ? (
                      <Link
                        to="/setup/departments/$id"
                        params={{ id: user.departmentId }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {department.data.data.name}
                      </Link>
                    ) : user.departmentId ? (
                      <Muted>loading…</Muted>
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </DetailRow>
                  <DetailRow field="managerId" label="Manager">
                    {user.managerId && managerName ? (
                      <Link
                        to="/users/$id"
                        params={{ id: user.managerId }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {managerName}
                      </Link>
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </DetailRow>
                  <DetailRow field="srControllerId" label="SR Controller">
                    {user.srControllerId && srControllerName ? (
                      <Link
                        to="/users/$id"
                        params={{ id: user.srControllerId }}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {srControllerName}
                      </Link>
                    ) : (
                      <Muted>—</Muted>
                    )}
                  </DetailRow>
                  <DetailRow field="employeeType" label="Employee type">
                    {user.employeeType ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="hoursPerMonth" label="Hours / month">
                    {user.hoursPerMonth != null ? user.hoursPerMonth : <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="billRate" label="Bill rate">
                    {user.billRate != null ? `$${user.billRate} / hr` : <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="startDate" label="Start date">
                    {user.startDate ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="terminatedDate" label="Terminated">
                    {user.terminatedDate ?? <Muted>—</Muted>}
                  </DetailRow>
                </DetailGrid>
              </Section>

              {/* Customer-team section: shown only when the caller may see it AND
              the user has at least one membership (any time). customerTeamCount
              is 0 until the detail load resolves, so an employee with no teams
              never flashes an empty section. */}
              {showCustomerTeam === null ? (
                <Skeleton className="h-32 w-full rounded-md" />
              ) : showCustomerTeam && customerTeamCount > 0 ? (
                <UserCustomerTeamSection userId={id} />
              ) : null}

              {/* Direct reports: self-hides when the employee manages no one. */}
              <UserDirectReportsSection userId={id} />

              {costVisible ? (
                <Section
                  title="Employee cost"
                  description="Versioned cost history — current period at the top."
                  count={costRows.length}
                >
                  {costHistory.isLoading ? (
                    <Skeleton className="h-24 w-full" />
                  ) : costRows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No cost recorded. Use Change cost on the edit page to record one.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>From</TableHead>
                          <TableHead>To</TableHead>
                          <TableHead className="text-right">Cost / hour</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costRows.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell className="font-mono text-sm">{c.effectiveFrom}</TableCell>
                            <TableCell className="font-mono text-sm">
                              {c.effectiveTo ?? <Muted>open</Muted>}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              ${c.costPerHour}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Section>
              ) : (
                // Locked placeholder — the caller lacks cost access for this
                // employee (no user.cost.view, or outside its HR-Managed scope).
                // Surfaced (not hidden) so the field is discoverable without
                // leaking the value.
                <Section title="Employee cost">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock aria-hidden />
                    <span>Cost restricted — you don't have access to this employee's cost.</span>
                  </div>
                </Section>
              )}

              <Section
                title="Skills"
                count={skills.length}
                headerRight={
                  canEditSkills ? (
                    <div className="flex items-center gap-2">
                      <div className="w-64">
                        <SkillAutocomplete
                          value={null}
                          excludeIds={Array.from(alreadyAddedIds)}
                          disabled={addPending || addSkill.isPending}
                          placeholder="Add skill…"
                          onChange={(skill) => {
                            if (skill) {
                              setAddPending(true);
                              addSkill.mutate(skill.id);
                            }
                          }}
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBulkOpen(true)}
                        disabled={bulkAdd.isPending}
                      >
                        <Plus />
                        Bulk add
                      </Button>
                    </div>
                  ) : null
                }
              >
                <SkillsTable
                  skills={skills}
                  loading={skillsQuery.isLoading}
                  canEdit={canEditSkills}
                  disabled={removeSkill.isPending}
                  onRemove={(s) => {
                    if (confirm(`Remove "${s.name}" from this user?`)) {
                      removeSkill.mutate(s.id);
                    }
                  }}
                />
              </Section>

              <Section title="Contact" description="Phones — all optional.">
                <DetailGrid cols={3}>
                  <DetailRow field="workPhone" label="Work phone">
                    {user.workPhone ? formatPhone(user.workPhone) : <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="homePhone" label="Home phone">
                    {user.homePhone ? formatPhone(user.homePhone) : <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="cellPhone" label="Cell phone">
                    {user.cellPhone ? formatPhone(user.cellPhone) : <Muted>—</Muted>}
                  </DetailRow>
                </DetailGrid>
              </Section>

              {addressVisible ? (
                <Section
                  title="Address"
                  description="Flat address. Location is a free-text label that may differ from country/state."
                >
                  <DetailGrid>
                    <DetailRow label="Address">{renderAddress(user) ?? <Muted>—</Muted>}</DetailRow>
                    <DetailRow field="location" label="Location">
                      {user.location ?? <Muted>—</Muted>}
                    </DetailRow>
                  </DetailGrid>
                </Section>
              ) : (
                // Locked placeholder — the caller lacks user.pii.view for this
                // employee (and it isn't their own profile). The address fields on
                // `data` are redacted server-side; surfaced (not hidden) so the
                // section is discoverable without leaking the value.
                <Section title="Address">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock aria-hidden />
                    <span>Address restricted — you don't have access to this employee's PII.</span>
                  </div>
                </Section>
              )}

              <Section
                title="External IDs"
                description="Free-text correlation IDs. Each is unique per tenant where present."
              >
                <DetailGrid>
                  <DetailRow field="historicalTimesheetId" label="Historical timesheet ID">
                    {user.historicalTimesheetId ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="kekaEmployeeId" label="Keka employee ID">
                    {user.kekaEmployeeId ?? <Muted>—</Muted>}
                  </DetailRow>
                  <DetailRow field="quickbooksId" label="Quickbooks ID">
                    {user.quickbooksId ?? <Muted>—</Muted>}
                  </DetailRow>
                </DetailGrid>
              </Section>

              <Section
                title="Settings"
                description="IANA timezone drives midnight-split for timers."
              >
                <DetailGrid>
                  <DetailRow label="Timezone">{user.timezone}</DetailRow>
                  <DetailRow label="Created">{new Date(user.createdAt).toLocaleString()}</DetailRow>
                  <DetailRow label="Updated">{new Date(user.updatedAt).toLocaleString()}</DetailRow>
                </DetailGrid>
              </Section>

              <CustomFieldsViewer object="user" values={user.customFields ?? null} />
            </PreviousValues>
          </PillTabsContent>

          {canViewAudit ? (
            <PillTabsContent value="audit" className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Every state-changing operation, newest first — including employee cost (if
                permitted).
              </p>
              <div className="rounded-md border">
                <ScopedAuditPanel query={audit} />
              </div>
            </PillTabsContent>
          ) : null}
        </PillTabs>
      ) : null}

      {user ? (
        <BulkAddSkillsSheet
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          alreadyAddedIds={alreadyAddedIds}
          onSubmit={(picks) => bulkAdd.mutate(picks.map((s) => s.id))}
        />
      ) : null}

      {user ? (
        <UserChangeEmailSheet
          userId={id}
          currentEmail={user.email}
          open={emailOpen}
          onOpenChange={setEmailOpen}
        />
      ) : null}

      <UserFormSheet mode="edit" userId={id} open={editOpen} onOpenChange={setEditOpen} />
    </PageShell>
  );
}

function SkillsTable({
  skills,
  loading,
  canEdit,
  disabled,
  onRemove,
}: {
  skills: Skill[];
  loading: boolean;
  canEdit: boolean;
  disabled: boolean;
  onRemove: (skill: Skill) => void;
}) {
  if (loading) return <Skeleton className="h-24 w-full" />;
  if (skills.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {canEdit ? 'No skills yet. Add one via the picker or “Bulk add”.' : 'No skills yet.'}
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          {canEdit ? <TableHead aria-label="actions" /> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {skills.map((s) => (
          <TableRow key={s.id}>
            <TableCell className="font-medium">{s.name}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{s.description ?? '—'}</TableCell>
            {canEdit ? (
              <TableCell className="text-right">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label={`Remove ${s.name}`}
                  disabled={disabled}
                  onClick={() => onRemove(s)}
                >
                  <Trash2 />
                </Button>
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Responsive label/value grid for the dl-style sections (Identity,
 * Employment, …). 2 cols by default; pass `cols={3}` for short-value
 * sections like Contact phones.
 */
function DetailGrid({ cols = 2, children }: { cols?: 2 | 3; children: React.ReactNode }) {
  const colsClass = cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  return <dl className={`grid grid-cols-1 gap-x-6 gap-y-3 ${colsClass}`}>{children}</dl>;
}

/** This page's own row shape (a stacked label/value cell, not the 4-column
 *  table the customer page uses). `field` opts the row into showing the value
 *  the selected version replaced — see `PreviousValueMark`. */
function DetailRow({
  label,
  field,
  children,
}: {
  label: string;
  field?: string;
  children: React.ReactNode;
}) {
  // Same marks as the customer page's grid, from the same constants — this
  // screen has its own DetailRow, and the two must not drift on what a
  // changed field looks like. The rule spans BOTH lines here because the label
  // sits above the value rather than beside it.
  const changed = useFieldChanged(field);
  return (
    <div className={cn('space-y-0.5', changed && `${CHANGED_RULE_CLASS} -my-1 -ml-2.5 py-1 pl-2`)}>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">
        <FieldValue field={field}>{children}</FieldValue>
      </dd>
    </div>
  );
}

function SectionsSkeleton() {
  return (
    <>
      <section className="space-y-3 rounded-md border bg-card p-4">
        <Skeleton className="h-4 w-24" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={`skel-${i}`} className="h-10 w-full" />
          ))}
        </div>
      </section>
      <section className="space-y-3 rounded-md border bg-card p-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-32 w-full" />
      </section>
    </>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

/**
 * Render the multi-field address as a single string the detail row can
 * place inline. Returns null when none of the address fields are set so
 * the caller can render its em-dash placeholder.
 */
function renderAddress(
  u: Pick<
    import('@clockwise/clients/users').User,
    'addressLine1' | 'addressLine2' | 'city' | 'state' | 'zip' | 'country'
  >,
): string | null {
  const parts = [
    u.addressLine1,
    u.addressLine2,
    [u.city, u.state, u.zip].filter(Boolean).join(', '),
    // Stored as an ISO-3166 alpha-2 code; countryName falls back to the
    // raw value for legacy free-text rows.
    u.country ? countryName(u.country) : null,
  ].filter((p): p is string => !!p && p.trim().length > 0);
  return parts.length === 0 ? null : parts.join(' · ');
}
